import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { Col, Modal, Row } from 'react-bootstrap'
import { Button, Loading, Paragraph, Select } from 'src/components'
import { faSave } from '@fortawesome/free-solid-svg-icons'

import { getProviders } from 'src/actions/cardProvider.actions'
import { selectCardProviders } from 'src/selectors/cardProvider.selector'

import {
  actions,
  getPOSMethodPaymentConfiguration,
  savePOSMethodPaymentConfiguration,
} from 'src/actions/pos.actions'
import { selectPOSMethodPaymentConfiguration } from 'src/selectors/pos.selector'

import { loadingSelector } from 'src/selectors/loading.selector'
import { handlerError, handlerSuccess, hasErrors } from 'src/selectors/error.selector'
import { showAlert } from 'src/actions/alert.actions'
import { basicMethodPayments, ignoreAccountTypes } from 'src/enums/paymentTypes'
import { getAccounts } from 'src/actions/banks.actions'
import { selectGetAccountsForSelect } from 'src/selectors/banks.selector'

interface POSMethodPaymentsProps {
  show: boolean
  posId: number
  onHide: () => void
}

const defaultOption = { value: null, label: 'Sin seleccionar' }

const paymentMethods: ISelect[] = [defaultOption, ...basicMethodPayments]

export const POSMethodPayments = ({ show, posId, onHide }: POSMethodPaymentsProps) => {
  const dispatch = useDispatch()
  const providers = useSelector(selectCardProviders)
  const accounts = useSelector(selectGetAccountsForSelect)
  const accountsFiltered = accounts.filter(a => !ignoreAccountTypes.includes(a.bank))

  const configurationDTO = useSelector(selectPOSMethodPaymentConfiguration)
  const loadingGet = useSelector(state =>
    loadingSelector([actions.GET_POS_METHOD_PAYMENT_CONFIGURATION])(state),
  )
  const errorGet = useSelector(state =>
    hasErrors([actions.GET_POS_METHOD_PAYMENT_CONFIGURATION])(state),
  )

  const loadingSave = useSelector(state =>
    loadingSelector([actions.SAVE_POS_METHOD_PAYMENT_CONFIGURATION])(state),
  )
  const errorSave = useSelector(state =>
    hasErrors([actions.SAVE_POS_METHOD_PAYMENT_CONFIGURATION])(state),
  )

  const [flag, setFlag] = useState({
    get: false,
    save: false,
  })

  const options = providers.map(provider => ({
    value: provider.id,
    label: provider.name,
  }))

  const [configuration, setConfiguration] = useState<POSMethodPaymentConfiguration>({})

  useEffect(() => {
    if (!show) return
    dispatch(getProviders())
    dispatch(getPOSMethodPaymentConfiguration(posId))
    dispatch(getAccounts())
  }, [show])

  useEffect(() => {
    if (loadingGet) setFlag({ ...flag, get: true })
    else if (flag.get) {
      setFlag({ ...flag, get: false })
      if (errorGet)
        dispatch(
          showAlert({
            ...handlerError(errorGet.messsage),
            onConfirm: () => onHide(),
          }),
        )
      else {
        setConfiguration({
          posId,
          cardProvider: configurationDTO?.cardProvider,
          principalPaymentMethod: configurationDTO?.principalPaymentMethod,
          bankAccount: configurationDTO?.bankAccount,
        })
      }
    }
  }, [loadingGet])

  useEffect(() => {
    if (loadingSave) setFlag({ ...flag, save: true })
    else if (flag.save) {
      setFlag({ ...flag, save: false })

      const alert = errorSave ? handlerError(errorGet.messsage) : handlerSuccess()
      dispatch(showAlert(alert))
    }
  }, [loadingSave])

  const onClose = () => {
    onHide()
    setConfiguration({})
  }

  const onSave = () => {
    dispatch(savePOSMethodPaymentConfiguration(configuration))
  }

  const cardProviderSelected = useMemo(() => {
    if (configuration?.cardProvider) {
      const provider = options.find(option => option.value === configuration.cardProvider)
      if (provider) return provider
    }
    return defaultOption
  }, [configuration?.cardProvider])

  const bankAccount = useMemo(() => {
    if (configuration?.bankAccount) {
      const provider = accountsFiltered.find(
        option => option.value === configuration.bankAccount,
      )
      if (provider) return provider
    }
    return defaultOption
  }, [configuration?.bankAccount])

  const principalMethod = useMemo(() => {
    if (configuration?.principalPaymentMethod) {
      const method = paymentMethods.find(
        option => option.value === configuration.principalPaymentMethod,
      )
      if (method) return method
    }
    return defaultOption
  }, [configuration?.principalPaymentMethod])

  return (
    <Modal show={show} centered onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>Métodos de pago</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col xl={12}>
            <Loading show={loadingGet} />
          </Col>

          <Col xl={12}>
            <Paragraph size={'big'} bold>
              Método de pago predeterminado
            </Paragraph>
          </Col>
          <Col xl={12}>
            <Paragraph size={'medium'}>
              En la aplicación de kolo se auto seleccionará el tipo de pago indicado
            </Paragraph>
            <Select
              label={'Tipos de pago'}
              options={paymentMethods}
              value={principalMethod}
              onChange={({ value }) =>
                setConfiguration({ ...configuration, principalPaymentMethod: value })
              }
            />
          </Col>
          <Col xl={12}>
            <hr />
          </Col>

          <Col xl={12}>
            <Paragraph size={'big'} bold>
              Método: Transferencia
            </Paragraph>
          </Col>
          <Col xl={12}>
            <Paragraph size={'medium'}>
              En la aplicación de kolo se auto seleccionará la cuenta bancaria indicada
            </Paragraph>
            <Select
              label={'Cuentas bancarias'}
              options={[defaultOption, ...accountsFiltered]}
              value={bankAccount}
              onChange={({ value }) =>
                setConfiguration({ ...configuration, bankAccount: value })
              }
            />
          </Col>

          <Col xl={12}>
            <hr />
          </Col>

          <Col xl={12}>
            <Paragraph size={'big'} bold>
              Método: Tarjeta
            </Paragraph>
          </Col>
          <Col xl={12}>
            <Paragraph size={'medium'}>
              Configura un proveedor de cobros predeterminado
            </Paragraph>
            <Select
              label={'Proveedor'}
              options={[defaultOption, ...options]}
              value={cardProviderSelected}
              onChange={({ value }) =>
                setConfiguration({ ...configuration, cardProvider: value })
              }
            />
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Row className={'container-buttons'}>
          <Button
            icon={faSave}
            onClick={onSave}
            disabled={loadingGet}
            loading={loadingSave}>
            Guardar
          </Button>
        </Row>
      </Modal.Footer>
    </Modal>
  )
}
